// The Tariff Usage Report by NMI: for one NMI, which days in a month range have no
// interval data. A day counts as covered when bv_nem_interval holds any row
// for the site on that date (any suffix, any provider) — the same rule the
// bill run's coverage check uses (bv_bill_run_queue.days_covered).
//
// Dates are `YYYY-MM-DD` strings, compared as strings so no time zone can move
// a day into the neighbouring month.

import { monthEnd, monthsInRange } from './billMonths';

export type MonthGap = {
  month: string;          // YYYY-MM
  daysExpected: number;   // days in the month up to `lastDay`
  missingDays: string[];  // YYYY-MM-DD, ascending
};

/** Every date from `from` to `to`, inclusive. */
function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  for (; d <= end; d.setUTCDate(d.getUTCDate() + 1)) out.push(d.toISOString().slice(0, 10));
  return out;
}

/**
 * One row per month from `fromMonth` to `toMonth`. Days after `lastDay`
 * (normally yesterday — today's data has not landed yet) are not expected; a
 * month wholly after it is dropped.
 */
export function usageGaps(covered: Set<string>, fromMonth: string, toMonth: string, lastDay: string): MonthGap[] {
  const rows: MonthGap[] = [];
  for (const month of monthsInRange(fromMonth, toMonth)) {
    const first = `${month}-01`;
    if (first > lastDay) break;
    const end = monthEnd(month) < lastDay ? monthEnd(month) : lastDay;
    const days = daysBetween(first, end);
    rows.push({ month, daysExpected: days.length, missingDays: days.filter((d) => !covered.has(d)) });
  }
  return rows;
}

/** Yesterday in `timeZone`, as `YYYY-MM-DD`. */
export function yesterday(now: Date = new Date(), timeZone = 'Australia/Sydney'): string {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone }).format(now);
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
